import { type PropsWithChildren } from "react";

import { observer } from "mobx-react-lite";

import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useCursorContextId } from "@/components/input/CursorContextProvider";
import { NodeKeyboardInput } from "@/components/input/NodeKeyboardInput";
import { twMerge } from "@/utils/twMerge";

import { SuggestionsProvider } from "../suggestions/SuggestionsProvider";

import type { SuggestionProviderType } from "../suggestions/types";
import type { NodeModel } from "@/models/nodes/types";

type Props = {
  node: NodeModel;
  className?: string;
  inputClassName?: string;
  disableEditingStyle?: boolean;
  accept?: SuggestionProviderType[];
};

export const EditNodeInput = observer(
  ({
    node,
    className,
    inputClassName,
    disableEditingStyle,
    accept,
    children,
  }: PropsWithChildren<Props>) => {
    const isEditing = node.doc.input.isEditing(node);
    const { renderContext } = useNodeStyle();
    const cursorContextId = useCursorContextId();

    const isCurrentCursorContext = cursorContextId === node.doc.cursorContextId;

    if (
      isEditing &&
      (renderContext === "editor" || renderContext === "chat-input") &&
      isCurrentCursorContext
    ) {
      return (
        <SuggestionsProvider accept={accept}>
          <NodeKeyboardInput
            doc={node.doc}
            node={node}
            className={twMerge("flex items-center before:content-zero-space", className)}
            inputClassName={inputClassName}
            disableEditingStyle={disableEditingStyle}
          />
        </SuggestionsProvider>
      );
    }

    return children;
  },
);
